import { useState, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useNodeId, useInternalNode } from "@xyflow/react";
import { Spinner, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import {
  togglesAtomResettable,
  saveCanvasSetter,
  nodesAtom,
} from "@zapier/canvas-state";

import {
  toEmbeddableUrl,
  isYouTubeShorts,
} from "../../../../utils/toEmbeddableUrl";
import {
  isImageUrl,
  isVideoUrl as isDirectVideoFile,
} from "@zapier/rich-text-editor";
import { getSandboxAttribute } from "../../../../utils/getSandboxAttribute";
import { getCurrentOrigin } from "../../../../utils/getCurrentOrigin";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { VideoPlayer } from "../../../Embeds";
import {
  isValidVideoUrl,
  isValidVideoProviderUrl,
} from "../../../Embeds/VideoPlayer";

import styles from "./EmbedNodeContent.module.css";

type DraggableEmbedOverlayProps = {
  ariaLabel: string;
  onClick: () => void;
};

/**
 * Transparent overlay button that captures clicks while allowing node dragging.
 * Sits on top of iframe to prevent embedded content from interfering with drag interactions.
 */
function DraggableEmbedOverlay({
  ariaLabel,
  onClick,
}: DraggableEmbedOverlayProps) {
  return (
    <button
      type="button"
      className={styles.contentOverlay}
      onClick={onClick}
      aria-label={ariaLabel}
    />
  );
}

type Props = {
  url: string;
};

export function EmbedNodeContent({ url }: Props) {
  const nodeId = useNodeId();
  const node = useInternalNode(nodeId!);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [isOverlayHidden, setIsOverlayHidden] = useState(false);

  const embeddableUrl = toEmbeddableUrl(url);
  const isImage = isImageUrl(embeddableUrl);
  const isVideoUrl = isValidVideoUrl(embeddableUrl);
  const isDirectVideo = isDirectVideoFile(embeddableUrl);
  const isPlatformVideoEmbed = isValidVideoProviderUrl(embeddableUrl);
  const isYTShorts = isYouTubeShorts(url); // Check original URL before conversion

  const { trusted_embed_domains } = useAtomValue(togglesAtomResettable);
  const currentOrigin = getCurrentOrigin();
  const updateNodeAndSave = useUpdateNode();

  const sandboxAttr = getSandboxAttribute(
    url,
    trusted_embed_domains.config.domains,
    currentOrigin,
  );

  const handleOverlayClick = useCallback(() => {
    if (isPlatformVideoEmbed) {
      // For platform video embeds: enable autoplay (only set once, URL shouldn't change again)
      setShouldAutoplay(true);
    }
    // For iframe embeds: hide overlay to allow interaction
    setIsOverlayHidden(true);
  }, [isPlatformVideoEmbed]);

  const handleMouseLeave = useCallback(() => {
    // Restore overlay to make iframe embeds draggable again
    // Don't reset shouldAutoplay (prevents video restart)
    setIsOverlayHidden(false);
  }, []);

  const handleIframeLoad = useAtomCallback(
    useCallback(
      (get, set) => {
        setIsLoading(false);

        // For iframes, we can't detect dimensions due to CORS.
        // On first init, save to backend to persist the default dimensions (width/height/measured)
        // that were set when the node was created. Skip on page refresh when dimensions already exist.
        if (!node?.width || !node?.height) {
          set(saveCanvasSetter, { nodes: get(nodesAtom) });
        }
      },
      [node?.width, node?.height],
    ),
  );

  const handleContentError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const setInitialNodeSize = useCallback(
    (width: number, height: number) => {
      setIsLoading(false);

      // Only set dimensions on first load, not on page refresh when dimensions already exist
      if (node?.width && node?.height) {
        return;
      }

      updateNodeAndSave({
        width,
        height,
        measured: {
          width,
          height,
        },
      });
    },
    [node?.width, node?.height, updateNodeAndSave],
  );

  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      const { naturalWidth, naturalHeight } = img;

      // Auto-size images down on initial load to prevent large images from dominating canvas.
      // Uses same scaling logic as videos (350-450px range). Only triggers on first load,
      // not on page refresh - existing node dimensions are preserved via setInitialNodeSize.
      const maxWidth = 450;
      const minWidth = 350;
      const scaleFactor = 10;

      const targetWidth = Math.max(
        minWidth,
        Math.min(maxWidth, naturalWidth / scaleFactor),
      );
      const targetHeight = (targetWidth / naturalWidth) * naturalHeight;

      // Update node size to calculated dimensions
      setInitialNodeSize(targetWidth, targetHeight);
      setIsLoading(false);
    },
    [setInitialNodeSize],
  );

  // Reset loading/error state when URL changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [url]);

  const hostname = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  })();

  if (hasError) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <Icon name="alertCircleFill" color="StrokeStronger" size={32} />
          <Text type="ParagraphHeader1" color="TextWeaker">
            Failed to load content
          </Text>
          <Text type="Body1" color="TextWeaker">
            This website may not allow embedding or there might be a connection
            issue.
          </Text>
        </div>
      </div>
    );
  }

  // Determine if overlay should be shown (blocks interaction, enables dragging)
  // Images and direct video files don't need overlay - they don't require interaction to activate
  const shouldShowOverlay = !isImage && !isDirectVideo && !isOverlayHidden;

  return (
    <div className={styles.container} onMouseLeave={handleMouseLeave}>
      {isImage ? (
        <img
          src={embeddableUrl}
          alt="Embedded content"
          className={styles.image}
          onLoad={handleImageLoad}
          onError={handleContentError}
          draggable={false}
        />
      ) : (
        <div className={styles.contentWrapper}>
          {isVideoUrl ? (
            <VideoPlayer
              url={embeddableUrl}
              shouldAutoplay={shouldAutoplay}
              updateNodeSize={setInitialNodeSize}
              isYouTubeShorts={isYTShorts}
              onIframeLoad={handleIframeLoad}
            />
          ) : (
            <>
              {isLoading && (
                <div className={styles.loadingContainer}>
                  <Spinner />
                </div>
              )}
              <iframe
                src={embeddableUrl}
                title={`Embedded content from ${hostname}`}
                className={styles.iframe}
                sandbox={sandboxAttr}
                onLoad={handleIframeLoad}
                onError={handleContentError}
              />
            </>
          )}
          {shouldShowOverlay && (
            <DraggableEmbedOverlay
              ariaLabel={
                isPlatformVideoEmbed
                  ? "Click to play video"
                  : "Click to interact"
              }
              onClick={handleOverlayClick}
            />
          )}
        </div>
      )}
    </div>
  );
}
